import { motion } from 'framer-motion'
import { Smartphone, Monitor, Tablet, ArrowRight, Download, ExternalLink } from 'lucide-react'

const AppDetails = () => {
  const apps = [
    {
      id: 'aluno',
      name: 'App do Aluno',
      description: 'Acompanhe seu progresso, veja avaliações, troféus conquistados e gerencie pagamentos.',
      icon: Smartphone,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      features: [
        'Visualize seu nível atual e requisitos',
        'Acompanhe avaliações dos professores',
        'Sistema de troféus e conquistas',
        'Gestão de mensalidades e pagamentos',
        'Avalie a escola e professores',
        'Comunicação direta com professores'
      ],
      screenshots: [
        '/SwimmingPool Aluno/prints iphone e android/tela_home.jpeg',
        '/SwimmingPool Aluno/prints iphone e android/tela_meus_trofeus.jpeg',
        '/SwimmingPool Aluno/prints iphone e android/tela_minhas_avaliacoes.jpeg',
        '/SwimmingPool Aluno/prints iphone e android/tela_pagamentos_mensalidades_em_aberto.png'
      ],
      platforms: ['iOS', 'Android'],
      downloadLinks: {
        ios: '#',
        android: '#'
      }
    },
    {
      id: 'escola',
      name: 'App da Escola',
      description: 'Gerencie alunos, turmas, professores, mensalidades e tenha controle total da sua escola.',
      icon: Monitor,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      features: [
        'Gestão completa de alunos e turmas',
        'Controle de mensalidades e receitas',
        'Relatórios financeiros detalhados',
        'Agenda diária e semanal',
        'Sistema de mensagens interno',
        'Indicadores e KPIs'
      ],
      screenshots: [
        '/SwimmingPool Escola/prints Iphone 16 pro max/tela_home_rsumo_geral.png',
        '/SwimmingPool Escola/prints Iphone 16 pro max/tela_pagamentos_indicadores_kpis.png',
        '/SwimmingPool Escola/prints Iphone 16 pro max/tela_agenda_semanal.png',
        '/SwimmingPool Escola/prints Iphone 16 pro max/tela_listagem-turmas.png'
      ],
      platforms: ['Web', 'iOS', 'Android'],
      downloadLinks: {
        web: '#',
        ios: '#',
        android: '#'
      }
    },
    {
      id: 'professor',
      name: 'App do Professor',
      description: 'Gerencie avaliações, acompanhe alunos e mantenha comunicação direta através de um dashboard completo.',
      icon: Tablet,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      features: [
        'Dashboard inicial com visão geral',
        'Sistema completo de avaliações',
        'Listagem de alunos para avaliação',
        'Histórico de avaliações realizadas',
        'Agenda pessoal do professor',
        'Chat com alunos e conversas'
      ],
      screenshots: [
        '/SwimmingPool Professor/tela_dashboard_inicial.jpeg',
        '/SwimmingPool Professor/tela_avaliacao_listagem_alunos.jpeg',
        '/SwimmingPool Professor/tela_avaliando_aluno.jpeg',
        '/SwimmingPool Professor/tela_minha_agenda.jpeg'
      ],
      platforms: ['iOS', 'Android'],
      downloadLinks: {
        ios: '#',
        android: '#'
      }
    }
  ]

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {apps.map((app, index) => (
          <motion.div
            key={app.id}
            id={app.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: index * 0.2 }}
            className={`mb-20 ${index !== 0 ? 'pt-20 border-t border-gray-200' : ''}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${app.color} text-white`}>
                    <app.icon className="h-8 w-8" />
                  </div>
                  
                  <h2 className="text-3xl lg:text-4xl font-bold text-dark-900">
                    {app.name}
                  </h2>
                  
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {app.description}
                  </p>
                </div>

                {/* Features */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-dark-900">Principais funcionalidades:</h3>
                  <ul className="space-y-3">
                    {app.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${app.color} mt-2 flex-shrink-0`}></div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Platforms */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-dark-900">Plataformas disponíveis:</h3>
                  <div className="flex flex-wrap gap-3">
                    {app.platforms.map((platform) => (
                      <span 
                        key={platform}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium"
                      >
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Download Links */}
                <div className="flex flex-col sm:flex-row gap-4">
                  {app.platforms.includes('iOS') && (
                    <a 
                      href={app.downloadLinks.ios}
                      className="btn-primary inline-flex items-center justify-center"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download iOS
                    </a>
                  )}
                  
                  {app.platforms.includes('Android') && (
                    <a 
                      href={app.downloadLinks.android}
                      className="btn-outline inline-flex items-center justify-center"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download Android
                    </a>
                  )}
                  
                  {app.platforms.includes('Web') && (
                    <a 
                      href={app.downloadLinks.web}
                      className="btn-secondary inline-flex items-center justify-center"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Acessar Web
                    </a>
                  )}
                </div>
              </div>

              {/* Screenshots */}
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  {app.screenshots.map((screenshot, screenshotIndex) => (
                    <motion.div
                      key={screenshotIndex}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: screenshotIndex * 0.1 }}
                      className="group"
                    >
                      <img 
                        src={screenshot} 
                        alt={`${app.name} Screenshot ${screenshotIndex + 1}`}
                        className="w-full h-auto rounded-lg shadow-lg border border-gray-200 group-hover:shadow-xl transition-shadow duration-300"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  )
}

export default AppDetails
